import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider, useAuthContext } from '@/contexts/AuthContext';
import { useMyStoresQuery } from '@/hooks/queries/useMyStoresQuery';
import { Loader2 } from 'lucide-react';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { FcmTokenSync, NotificationListener, NotificationPrompt } from '@/components/notifications';
import { AnalyticsIdentity } from '@/components/analytics/AnalyticsIdentity';
import { LifecycleEmailAttribution } from '@/components/analytics/LifecycleEmailAttribution';
import { trackEvent } from '@/lib/analytics';

/**
 * Pages and layouts are lazy-loaded so the main bundle only carries the boot shell. Each
 * page becomes its own chunk that downloads when the user navigates to it; cold opens that
 * land on `/login` no longer pay to parse the dashboard tree.
 */
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));
const StoreDashboardLayout = lazy(() => import('@/layouts/StoreDashboardLayout').then((m) => ({ default: m.StoreDashboardLayout })));
const StoreOverviewPage = lazy(() => import('@/pages/StoreOverviewPage').then((m) => ({ default: m.StoreOverviewPage })));
const StoreMembersPage = lazy(() => import('@/pages/StoreMembersPage').then((m) => ({ default: m.StoreMembersPage })));
const StoreMemberDetailPage = lazy(() => import('@/pages/StoreMemberDetailPage').then((m) => ({ default: m.StoreMemberDetailPage })));
const SettingsProfileRedirectPage = lazy(() => import('@/pages/SettingsProfileRedirectPage').then((m) => ({ default: m.SettingsProfileRedirectPage })));
const StoreBrandingPage = lazy(() => import('@/pages/StoreBrandingPage').then((m) => ({ default: m.StoreBrandingPage })));
const StoreFeaturesSectionLayout = lazy(() => import('@/layouts/StoreFeaturesSectionLayout').then((m) => ({ default: m.StoreFeaturesSectionLayout })));
const StoreFeaturesBillingPage = lazy(() => import('@/pages/StoreFeaturesBillingPage').then((m) => ({ default: m.StoreFeaturesBillingPage })));
const StoreSmsConfigurationPage = lazy(() => import('@/pages/StoreSmsConfigurationPage').then((m) => ({ default: m.StoreSmsConfigurationPage })));
const StoreEmployeesPage = lazy(() => import('@/pages/StoreEmployeesPage').then((m) => ({ default: m.StoreEmployeesPage })));
const StoreEmployeeDetailPage = lazy(() => import('@/pages/StoreEmployeeDetailPage').then((m) => ({ default: m.StoreEmployeeDetailPage })));
const StorePackagesPage = lazy(() => import('@/pages/StorePackagesPage').then((m) => ({ default: m.StorePackagesPage })));
const StorePackageDetailPage = lazy(() => import('@/pages/StorePackageDetailPage').then((m) => ({ default: m.StorePackageDetailPage })));
const StorePromotionsPage = lazy(() => import('@/pages/StorePromotionsPage').then((m) => ({ default: m.StorePromotionsPage })));
const StoreCampaignsPage = lazy(() => import('@/pages/StoreCampaignsPage').then((m) => ({ default: m.StoreCampaignsPage })));
const StoreCampaignDetailPage = lazy(() => import('@/pages/StoreCampaignDetailPage').then((m) => ({ default: m.StoreCampaignDetailPage })));
const StoreCampaignEditorPage = lazy(() => import('@/pages/StoreCampaignEditorPage').then((m) => ({ default: m.StoreCampaignEditorPage })));
const StoreSessionsPage = lazy(() => import('@/pages/StoreSessionsPage').then((m) => ({ default: m.StoreSessionsPage })));
const StoreClassesPage = lazy(() => import('@/pages/StoreClassesPage').then((m) => ({ default: m.StoreClassesPage })));
const StoreLocationsAddonPage = lazy(() => import('@/pages/StoreLocationsAddonPage').then((m) => ({ default: m.StoreLocationsAddonPage })));
const StorePaymentsPage = lazy(() => import('@/pages/StorePaymentsPage').then((m) => ({ default: m.StorePaymentsPage })));
const StoreGeneralSettingsPage = lazy(() => import('@/pages/StoreGeneralSettingsPage').then((m) => ({ default: m.StoreGeneralSettingsPage })));
const StoreCustomDomainPage = lazy(() => import('@/pages/StoreCustomDomainPage').then((m) => ({ default: m.StoreCustomDomainPage })));
const StoreContactPage = lazy(() => import('@/pages/StoreContactPage').then((m) => ({ default: m.StoreContactPage })));

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthContext();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function StoreShellSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/** Dashboard routes that require at least one owned store. */
function RequireOwnedStore({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const { data: stores = [], isLoading } = useMyStoresQuery(user?.uid);

  if (isLoading) {
    return <StoreShellSpinner />;
  }
  if (stores.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/** Onboarding is only for authenticated users who do not have a store yet. */
function RequireNoOwnedStore({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const { data: stores = [], isLoading } = useMyStoresQuery(user?.uid);

  if (isLoading) {
    return <StoreShellSpinner />;
  }
  if (stores.length > 0) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function RoutePageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackEvent('page_view', { path: location.pathname + location.search });
  }, [location.pathname, location.search]);
  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<StoreShellSpinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <RequireNoOwnedStore>
                <OnboardingPage />
              </RequireNoOwnedStore>
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <RequireOwnedStore>
                <StoreDashboardLayout />
              </RequireOwnedStore>
            </ProtectedRoute>
          }
        >
          <Route index element={<StoreOverviewPage />} />
          <Route path="gym/members" element={<StoreMembersPage />} />
          <Route path="gym/members/:userId" element={<StoreMemberDetailPage />} />
          <Route path="gym/promotions" element={<StorePromotionsPage />} />
          <Route path="gym/campaigns" element={<StoreCampaignsPage />} />
          <Route path="gym/campaigns/new" element={<StoreCampaignEditorPage />} />
          <Route path="gym/campaigns/:campaignId/edit" element={<StoreCampaignEditorPage />} />
          <Route path="gym/campaigns/:campaignId" element={<StoreCampaignDetailPage />} />
          <Route path="management/employees" element={<StoreEmployeesPage />} />
          <Route path="management/employees/:employeeId" element={<StoreEmployeeDetailPage />} />
          <Route path="management/packages" element={<StorePackagesPage />} />
          <Route path="management/packages/:packageId" element={<StorePackageDetailPage />} />
          <Route path="management/classes" element={<StoreClassesPage />} />
          <Route path="management/sessions" element={<StoreSessionsPage />} />
          <Route path="management/payments" element={<StorePaymentsPage />} />
          <Route path="management/branding" element={<StoreBrandingPage />} />
          <Route path="management/features" element={<StoreFeaturesSectionLayout />}>
            <Route index element={<StoreFeaturesBillingPage />} />
            <Route path="sms" element={<StoreSmsConfigurationPage />} />
            <Route path="locations" element={<StoreLocationsAddonPage />} />
          </Route>
          <Route path="management/sms" element={<Navigate to="/management/features/sms" replace />} />
          <Route path="management/locations" element={<Navigate to="/management/features/locations" replace />} />
          {/* Bug #7 — redirect intuitive shorthand paths to their canonical routes */}
          <Route path="members" element={<Navigate to="/gym/members" replace />} />
          <Route path="promotions" element={<Navigate to="/gym/promotions" replace />} />
          <Route path="campaigns" element={<Navigate to="/gym/campaigns" replace />} />
          <Route path="gym/settings" element={<Navigate to="/settings" replace />} />
          <Route path="employees" element={<Navigate to="/management/employees" replace />} />
          <Route path="packages" element={<Navigate to="/management/packages" replace />} />
          <Route path="classes" element={<Navigate to="/management/classes" replace />} />
          <Route path="sessions" element={<Navigate to="/management/sessions" replace />} />
          <Route path="settings" element={<StoreGeneralSettingsPage />} />
          <Route path="settings/custom-domain" element={<StoreCustomDomainPage />} />
          <Route path="contact" element={<StoreContactPage />} />
          <Route path="settings/contact" element={<Navigate to="/contact" replace />} />
          <Route path="settings/profile" element={<SettingsProfileRedirectPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// Notification and FCM components wrapper - only active when user is logged in
function AuthenticatedFcmComponents() {
  const { user } = useAuthContext();

  // Only active when user is logged in
  if (!user) return null;

  return (
    <>
      <NotificationListener />
      <FcmTokenSync />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider delayDuration={400}>
          <RoutePageViewTracker />
          <AppRoutes />
        </TooltipProvider>
        <Toaster />
        <NotificationPrompt />
        <AuthenticatedFcmComponents />
        <AnalyticsIdentity />
        <LifecycleEmailAttribution />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
